import React, { useEffect, useState } from 'react'
import './AdminBooking.css'
import Header from '../components/Header'
import axios from 'axios'
import { Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js'
import QRCodeGenerator from '../components/QRCodeGenerator'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler)

const getAuth = () =>
  document.cookie.replace(/(?:(?:^|.*;\s*)auth\s*=\s*([^;]*).*$)|^.*$/, "$1")

const AdminBookingPage = () => {
  const [bookings, setBookings] = useState([])
  const [filteredBookings, setFilteredBookings] = useState([])
  const [selectedDate, setSelectedDate] = useState('')
  const [selectedSlotTime, setSelectedSlotTime] = useState('all')
  const [chartData, setChartData] = useState(null)
  const [viewMode, setViewMode] = useState('sales')
  const [summary, setSummary] = useState({ totalBookings: 0, totalRevenue: 0, amountTobePaid: 0 })
  const [paymentAmount, setPaymentAmount] = useState('')
  const [payments, setPayments] = useState([])
  const [amountPaid, setAmountPaid] = useState(0)
  const [gymId, setGymId] = useState('')
  const [paymentMsg, setPaymentMsg] = useState(null)

  const fetchGymData = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/fetch`, {
        headers: { auth: getAuth() }
      })
      setGymId(response?.data?.gym?.id)
    } catch (error) {
      console.error('Error fetching gym data:', error)
    }
  }

  const getAllBooking = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/booking`, {
        headers: { auth: getAuth() }
      })
      const data = response.data.Booking
      setBookings(data)
      setFilteredBookings(data)
      generateChartData(data, viewMode)
      calculateSummary(data)
    } catch (error) {
      console.error('Error fetching bookings:', error)
    }
  }

  const getPayments = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/banking/get_payment`, {
        headers: { auth: getAuth() }
      })
      const data = response.data.payments
      setPayments(data)
      const totalPaid = data.reduce((sum, p) => sum + p.amount_paid, 0)
      setAmountPaid(totalPaid)
    } catch (error) {
      console.error('Error fetching payments:', error)
    }
  }

  useEffect(() => {
    getAllBooking()
    getPayments()
    fetchGymData()
  }, [])

  useEffect(() => {
    let filtered = bookings
    if (selectedDate) filtered = filtered.filter(b => b.bookingDate === selectedDate)
    if (selectedSlotTime !== 'all') filtered = filtered.filter(b => b.slotStartTime === selectedSlotTime)
    setFilteredBookings(filtered)
  }, [selectedDate, selectedSlotTime, bookings])

  const calculateSummary = (data) => {
    const totalRevenue = data.reduce((sum, b) => b.isPaid ? sum + b.subscriptionPrice : sum, 0)
    const amountTobePaid = totalRevenue - (totalRevenue * 8) / 100
    setSummary({ totalBookings: data.length, totalRevenue, amountTobePaid })
  }

  const generateChartData = (data, mode) => {
    const byDate = data.reduce((acc, b) => {
      const date = new Date(b.bookingDate).toLocaleDateString()
      acc[date] = acc[date] || { sales: 0, count: 0 }
      acc[date].sales += b.subscriptionPrice
      acc[date].count += 1
      return acc
    }, {})
    const labels = Object.keys(byDate)
    const values = labels.map(d => byDate[d][mode])
    setChartData({
      labels,
      datasets: [{
        label: mode === 'sales' ? 'Revenue (INR)' : 'Bookings',
        data: values,
        borderColor: '#10b981',
        backgroundColor: 'rgba(16,185,129,0.08)',
        fill: true,
        tension: 0.4,
        pointBackgroundColor: '#10b981',
        pointRadius: 4,
      }],
    })
  }

  const handleViewMode = (mode) => {
    setViewMode(mode)
    generateChartData(bookings, mode)
  }

  const markComplete = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/banking/complete_profile`, {}, {
        headers: { auth: getAuth() }
      })
      showMsg('Profile marked as complete', 'success')
    } catch {
      showMsg('Error marking profile', 'error')
    }
  }

  const handlePayment = async () => {
    if (!paymentAmount || isNaN(paymentAmount) || Number(paymentAmount) <= 0) {
      showMsg('Please enter a valid amount', 'error')
      return
    }
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/banking/make_payment`,
        { amount: paymentAmount, cred: localStorage.getItem('auth_data') },
        { headers: { auth: getAuth() } }
      )
      if (response.status === 200) {
        showMsg('Payment submitted successfully', 'success')
        setPaymentAmount('')
        getPayments()
      } else {
        showMsg('Error making payment', 'error')
      }
    } catch {
      showMsg('Error making payment', 'error')
    }
  }

  const showMsg = (text, type) => {
    setPaymentMsg({ text, type })
    setTimeout(() => setPaymentMsg(null), 3500)
  }

  const uniqueSlots = [...new Set(bookings.map(b => b.slotStartTime).filter(Boolean))]
  const remainingAmount = Math.floor(summary.amountTobePaid - amountPaid)

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#0d1117',
        borderColor: 'rgba(255,255,255,0.1)',
        borderWidth: 1,
        titleColor: '#f1f5f9',
        bodyColor: '#94a3b8',
      },
    },
    scales: {
      x: {
        grid: { color: 'rgba(255,255,255,0.04)' },
        ticks: { color: '#64748b', font: { size: 11 } },
      },
      y: {
        grid: { color: 'rgba(255,255,255,0.04)' },
        ticks: { color: '#64748b', font: { size: 11 } },
      },
    },
  }

  return (
    <div className="abk-page">
      <Header />
      <div className="abk-content">

        {/* Page Header */}
        <div className="abk-page-header">
          <div>
            <h1 className="abk-page-title">Bookings & Revenue</h1>
            <p className="abk-page-sub">Track bookings, revenue, and platform payments</p>
          </div>
          <QRCodeGenerator gymId={gymId} />
        </div>

        {/* Summary Stats */}
        <div className="abk-stats-grid">
          <div className="abk-stat-card">
            <div className="abk-stat-icon">📋</div>
            <div className="abk-stat-value">{summary.totalBookings}</div>
            <div className="abk-stat-label">Total Bookings</div>
          </div>
          <div className="abk-stat-card">
            <div className="abk-stat-icon">💰</div>
            <div className="abk-stat-value">₹{Math.floor(summary.totalRevenue).toLocaleString()}</div>
            <div className="abk-stat-label">Total Revenue</div>
          </div>
          <div className="abk-stat-card">
            <div className="abk-stat-icon">🏦</div>
            <div className="abk-stat-value">₹{Math.floor(summary.amountTobePaid).toLocaleString()}</div>
            <div className="abk-stat-label">Payable (after 8% fee)</div>
          </div>
          <div className="abk-stat-card">
            <div className="abk-stat-icon">✅</div>
            <div className="abk-stat-value">₹{Math.floor(amountPaid).toLocaleString()}</div>
            <div className="abk-stat-label">Amount Paid</div>
          </div>
          <div className={`abk-stat-card ${remainingAmount > 0 ? 'abk-stat-alert' : ''}`}>
            <div className="abk-stat-icon">⏳</div>
            <div className="abk-stat-value">₹{remainingAmount > 0 ? remainingAmount.toLocaleString() : 0}</div>
            <div className="abk-stat-label">Remaining</div>
          </div>
        </div>

        {/* Chart + Payment side by side */}
        <div className="abk-main-grid">
          {/* Chart */}
          <div className="abk-card">
            <div className="abk-card-head">
              <h2>Revenue Chart</h2>
              <div className="abk-tab-group">
                <button
                  className={`abk-tab ${viewMode === 'sales' ? 'active' : ''}`}
                  onClick={() => handleViewMode('sales')}
                >Revenue</button>
                <button
                  className={`abk-tab ${viewMode === 'count' ? 'active' : ''}`}
                  onClick={() => handleViewMode('count')}
                >Bookings</button>
              </div>
            </div>
            <div className="abk-chart-wrap">
              {chartData ? <Line data={chartData} options={chartOptions} /> : (
                <div className="abk-empty">No booking data yet</div>
              )}
            </div>
          </div>

          {/* Payment Panel */}
          <div className="abk-card">
            <div className="abk-card-head">
              <h2>Make Payment</h2>
            </div>
            <div className="abk-card-body">
              {paymentMsg && (
                <div className={`abk-msg ${paymentMsg.type}`}>{paymentMsg.text}</div>
              )}
              <div className="abk-form-group">
                <label>Payment Amount (INR)</label>
                <input
                  type="number"
                  value={paymentAmount}
                  onChange={e => setPaymentAmount(e.target.value)}
                  placeholder="Enter amount"
                  min="0"
                />
              </div>
              <button className="abk-btn-primary" onClick={handlePayment}>Submit Payment</button>
              <div className="abk-divider" />
              <button className="abk-btn-ghost" onClick={markComplete}>Mark Profile Complete</button>
            </div>
          </div>
        </div>

        {/* Filters + Bookings Table */}
        <div className="abk-card abk-table-card">
          <div className="abk-card-head">
            <h2>Booking Records</h2>
            <div className="abk-filters">
              <input
                type="date"
                className="abk-filter-input"
                value={selectedDate}
                onChange={e => setSelectedDate(e.target.value)}
              />
              <select
                className="abk-filter-input"
                value={selectedSlotTime}
                onChange={e => setSelectedSlotTime(e.target.value)}
              >
                <option value="all">All Slots</option>
                {uniqueSlots.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="abk-table-wrap">
            {filteredBookings.length === 0 ? (
              <div className="abk-empty">No bookings found</div>
            ) : (
              <table className="abk-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Date</th>
                    <th>Slot</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBookings.map((b, i) => (
                    <tr key={b.id || i}>
                      <td>{i + 1}</td>
                      <td>{b.bookingDate}</td>
                      <td>{b.slotStartTime || '—'}</td>
                      <td>₹{b.subscriptionPrice}</td>
                      <td>
                        <span className={`abk-badge ${b.isPaid ? 'paid' : 'unpaid'}`}>
                          {b.isPaid ? 'Paid' : 'Unpaid'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Payment History */}
        <div className="abk-card abk-table-card">
          <div className="abk-card-head">
            <h2>Payment History</h2>
          </div>
          <div className="abk-table-wrap">
            {payments.length === 0 ? (
              <div className="abk-empty">No payments recorded yet</div>
            ) : (
              <table className="abk-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Amount (INR)</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p, i) => (
                    <tr key={p.id || i}>
                      <td>{i + 1}</td>
                      <td>₹{p.amount_paid?.toLocaleString()}</td>
                      <td>{new Date(p.createdAt).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

      </div>
    </div>
  )
}

export default AdminBookingPage
